import "./styles/Analysis.css"
import { useEffect, useState } from "react"
import AnalysisChart from "./AnalysisChart"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { fetchAllSummaries, fetchFilterOptions, fetchTotals } from "../../../actions/analysisActions"
import { AnalysisSummary, AnalysisTotal, TrendTrack } from "../../../types"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { useNavigate } from "react-router-dom"
import { KLogo, KSpan } from "kahuna-base-react-components"
import { HoveredIndex, PlatformsLineChartComponent } from "../trends/streams-daily/StreamsDaily"
import { lang } from "../../../constants/languages"
import { trends } from "../../../apis"
import ChartContainer from "./ChartContainer"

const Analysis = ({
  allSummaries,
  fetchAllSummaries,
  totals,
  fetchTotals,
  filterOptions,
  fetchFilterOptions,
  currency
}: {
  allSummaries: AnalysisSummary[]
  fetchAllSummaries: (
    dateKey: string,
    type: string,
    value: string | number,
    affiliate: boolean,
    startDate?: string,
    endDate?: string
  ) => Promise<void>
  totals: AnalysisTotal[]
  fetchTotals: () => Promise<void>
  filterOptions: { [key: string]: KSelectOption[] }
  fetchFilterOptions: () => Promise<void>
  currency: string
}) => {
  const [loading, setLoading] = useState(true)

  const monthRange = 12
  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth() - (monthRange - 1))
  const defaultEndDate = new Date()
  const [streamsDaily, setStreamsDaily] = useState([])
  const [lastTwoMonthsStreams, setLastTwoMonthsStreams] = useState([])

  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })
  const [platformHoveredIndex, setPlatformHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1
  })

  const navigate = useNavigate()

  const getTotalsAndAllSummaries = async () => {
    setLoading(true)
    const startDateYear = defaultStartDate.getFullYear()
    const startDateMonth = String(defaultStartDate.getMonth() + 1).padStart(2, "0")
    const endDateYear = defaultEndDate.getFullYear()
    const endDateMonth = String(defaultEndDate.getMonth() + 1).padStart(2, "0")
    const startDateParam = `${startDateYear}${startDateMonth}`
    const endDateParam = `${endDateYear}${endDateMonth}`
    try {
      await fetchTotals()
      await fetchAllSummaries("date", "platform", "all_platforms", false, startDateParam, endDateParam)
    } finally {
      setLoading(false)
    }
  }

  const getStreamsDailyTopTracks = () => {
    trends(`/streams_last_7_days/`, "get")
      .then((resp) => {
        if (resp.data.results) {
          let streamsDaily = resp.data.results
          if (streamsDaily.length >= 5) {
            const isrcList = streamsDaily.slice(0, 5).map((streamDaily: TrendTrack) => streamDaily.isrc)
            streamsDaily = streamsDaily.filter((streamsDaily: TrendTrack) => isrcList.includes(streamsDaily.isrc))
          }
          setStreamsDaily(streamsDaily)
        }
      })
      .catch(() => {})
  }

  const getLastTwoMonthsStreams = () => {
    trends(`/streams_last_two_months/`, "get")
      .then((resp) => {
        if (resp.data.results) {
          setLastTwoMonthsStreams(resp.data.results)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    if (!filterOptions && fetchFilterOptions) {
      fetchFilterOptions().then(() => {})
    }
    getTotalsAndAllSummaries().then(() => {})
    getStreamsDailyTopTracks()
    getLastTwoMonthsStreams()
  }, [])

  return (
    <div>
      <div className="grid" style={{ height: "68px" }}>
        <div
          className="flex pl-10 pr-10"
          style={{
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <div style={{ textAlign: "left", display: "inline-block", cursor: "pointer" }} onClick={() => navigate(-1)}>
            <KLogo hoverEnabled={true} width={48} height={48} logoColor={"white"} borderRadius={1} />
          </div>
          <div>
            <span className="powered-by-span">Powered by Kahuna</span>
          </div>
        </div>
      </div>
      <div className="w-[100vw] flex justify-center pb-8">
        <div className="max-w-[1000px] w-[90%]">
          {!loading && (
            <div className="flex flex-col justify-start gap-4">
              {/*<AnalysisTotals />*/}
              <div
                onMouseLeave={() => {
                  setPlatformHoveredIndex((prev) => {
                    return { ...prev, datasetIndex: -1, dataIndex: -1 }
                  })
                }}
                className="py-5 px-7 flex-1 flex flex-col gap-2 min-h-0 relative"
                style={{
                  borderRadius: "10px",
                  border: "1px solid var(--stroke-soft-200, #F3F3F3)",
                  background: "var(--bg-white-0, #FFF)",
                  boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
                }}
              >
                <div className="flex justify-center">
                  <KSpan
                    text={lang.trends.headers.last_two_months_stream}
                    fontSize={16}
                    color="#000"
                    fontWeight={500}
                  />
                </div>
                <div className="flex-grow min-h-0 w-full" style={{ height: 360 }}>
                  <PlatformsLineChartComponent
                    data={lastTwoMonthsStreams}
                    hoveredIndex={platformHoveredIndex}
                    setHoveredIndex={setPlatformHoveredIndex}
                  />
                </div>
              </div>
              <div className="flex w-full">
                <ChartContainer
                  initialData={allSummaries}
                  defaultStartDate={defaultStartDate}
                  defaultEndDate={defaultEndDate}
                  currency={currency}
                />
              </div>
              {streamsDaily.length > 0 && (
                <div
                  className="w-full p-5 flex flex-col justify-start gap-3"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid var(--stroke-soft-200, #F3F3F3)",
                    background: " var(--bg-white-0, #FFF)",
                    boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
                  }}
                >
                  <KSpan text={lang.overview.top_5_tracks} fontSize={18} fontWeight={500} color="#111" />
                  <div className="flex-grow min-h-0 w-full" style={{ height: 300 }}>
                    <PlatformsLineChartComponent
                      data={streamsDaily}
                      hoveredIndex={hoveredIndex}
                      setHoveredIndex={() => {}}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    totals: state.analysis.totals,
    currency: state.auth.user.preferredCurrency,
    user: state.auth.user,
    selectedDate: state.analysis.selectedDate,
    selectedFilter: state.analysis.selectedFilter,
    summaries: state.analysis.summaries,
    filterOptions: state.analysis.filterOptions,
    allSummaries: state.analysis.allSummaries
  }
}

export default connect(mapStateToProps, { fetchAllSummaries, fetchTotals, fetchFilterOptions })(Analysis)
